import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, formatINR } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Target, Trash, PiggyBank } from "@phosphor-icons/react";
import GoalPlanner from "@/components/GoalPlanner";
import { toast } from "sonner";

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", target_amount: "", saved_amount: "", target_date: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/goals");
      setGoals(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    await api.post("/goals", {
      title: form.title,
      target_amount: Number(form.target_amount),
      saved_amount: Number(form.saved_amount) || 0,
      target_date: form.target_date || null,
    });
    toast.success("Goal added");
    setOpen(false);
    setForm({ title: "", target_amount: "", saved_amount: "", target_date: "" });
    load();
  };

  const addSaving = async (goal, amt) => {
    const val = Number(amt);
    if (!val) return;
    await api.put(`/goals/${goal.id}`, {
      title: goal.title,
      target_amount: goal.target_amount,
      saved_amount: Math.min(goal.saved_amount + val, goal.target_amount),
      target_date: goal.target_date,
      category: goal.category,
    });
    toast.success(`Added ${formatINR(val)} to ${goal.title}`);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/goals/${id}`);
    setGoals((g) => g.filter((x) => x.id !== id));
  };

  const createFromPlanner = async ({ target, years, monthly }) => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + years);
    const targetDate = d.toISOString().slice(0, 10);
    await api.post("/goals", {
      title: `${formatINR(target)} in ${years}y`,
      target_amount: target,
      saved_amount: 0,
      target_date: targetDate,
    });
    toast.success("Goal created", { description: `Aim for about ${formatINR(monthly)}/month` });
    load();
  };

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Goals</h1>
          <p className="text-muted-foreground mt-1">Save towards what matters — a trip, a gadget, an emergency fund.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-goal-btn" className="rounded-full bg-primary hover:bg-primary/90 gap-2">
              <Plus size={18} weight="bold" /> New goal
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Create a goal</DialogTitle></DialogHeader>
            <form onSubmit={add} className="space-y-4 mt-2">
              <div>
                <Label>Goal title</Label>
                <Input data-testid="goal-title" value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="mt-1.5 h-11" placeholder="e.g. Emergency fund" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Target (₹)</Label>
                  <Input type="number" data-testid="goal-target" value={form.target_amount}
                    onChange={(e) => setForm({ ...form, target_amount: e.target.value })}
                    className="mt-1.5 h-11 font-mono" placeholder="300000" required />
                </div>
                <div>
                  <Label>Saved so far (₹)</Label>
                  <Input type="number" data-testid="goal-saved" value={form.saved_amount}
                    onChange={(e) => setForm({ ...form, saved_amount: e.target.value })}
                    className="mt-1.5 h-11 font-mono" placeholder="0" />
                </div>
              </div>
              <div>
                <Label>Target date <span className="text-muted-foreground font-normal">optional</span></Label>
                <Input type="date" data-testid="goal-date" value={form.target_date}
                  onChange={(e) => setForm({ ...form, target_date: e.target.value })}
                  className="mt-1.5 h-11" />
              </div>
              <Button type="submit" data-testid="goal-submit" className="w-full h-11 rounded-full bg-primary hover:bg-primary/90">
                Create goal
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <GoalPlanner onCreate={createFromPlanner} />

      {loading ? (
        <div className="h-40 flex items-center justify-center text-muted-foreground">Loading…</div>
      ) : goals.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center">
          <Target size={40} weight="duotone" className="text-muted-foreground mx-auto mb-3" />
          <p className="font-medium">No goals yet</p>
          <p className="text-sm text-muted-foreground mt-1">Set your first savings goal to track your progress.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {goals.map((g, i) => {
            const pct = Math.min(100, Math.round((g.saved_amount / g.target_amount) * 100)) || 0;
            const done = pct >= 100;
            return (
              <motion.div
                key={g.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="rounded-xl border border-border bg-card p-5"
                data-testid="goal-card"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center">
                      <PiggyBank size={18} weight="duotone" className="text-primary" />
                    </div>
                    <div>
                      <p className="font-heading font-semibold">{g.title}</p>
                      {g.target_date && (
                        <p className="text-xs text-muted-foreground">
                          by {new Date(g.target_date).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}
                        </p>
                      )}
                    </div>
                  </div>
                  <button onClick={() => remove(g.id)} data-testid={`delete-goal-${g.id}`}
                    className="text-muted-foreground hover:text-destructive transition-colors duration-200">
                    <Trash size={18} />
                  </button>
                </div>

                <div className="flex items-baseline justify-between mb-2">
                  <span className="font-mono font-semibold text-lg">{formatINR(g.saved_amount)}</span>
                  <span className="text-sm text-muted-foreground font-mono">of {formatINR(g.target_amount)}</span>
                </div>
                <Progress value={pct} className="h-2" />
                <p className={`text-xs mt-2 font-medium ${done ? "text-primary" : "text-muted-foreground"}`}>
                  {done ? "🎉 Goal reached!" : `${pct}% there`}
                </p>

                {!done && (
                  <div className="flex gap-2 mt-4">
                    {[1000, 5000, 10000].map((amt) => (
                      <Button key={amt} variant="outline" size="sm"
                        data-testid={`add-saving-${g.id}-${amt}`}
                        onClick={() => addSaving(g, amt)}
                        className="rounded-full text-xs flex-1">
                        +₹{amt >= 1000 ? `${amt / 1000}k` : amt}
                      </Button>
                    ))}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
